import React, { useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import DOMPurify from 'dompurify';
import { utils } from '@flex/lib';
import { DataGetApi } from '../../helpers/PostDataApi';
import config from '../../constants/config';
import './Announcement.css';

const { announcementSanitizeOptions, renderAnnouncementMarkdown } = utils.announcement;
const getDismissalKey = (type, updatedAt) => `flex-announcement-${type}-${updatedAt}`;
export const ANNOUNCEMENT_UPDATED_EVENT = 'announcement-updated';

const getSelectedLocationId = () => {
  const storedLocationId = localStorage.getItem('locationId');
  if (storedLocationId) {
    try {
      return JSON.parse(storedLocationId);
    } catch {
      return storedLocationId.replaceAll('"', '');
    }
  }

  try {
    const locationIds = JSON.parse(localStorage.getItem('locationArrIds') || '[]');
    if (Array.isArray(locationIds) && locationIds[0]) return locationIds[0];
    const locationDetails = JSON.parse(localStorage.getItem('locationPaydetails') || '{}');
    return locationDetails?._id || null;
  } catch {
    return null;
  }
};

export const renderAnnouncementMessage = (message) => {
  const html = renderAnnouncementMarkdown(message);

  return (
    <span
      className="announcementMessage"
      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html, announcementSanitizeOptions) }}
    />
  );
};

const Announcement = () => {
  const [announcement, setAnnouncement] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const updateAnnouncement = (nextAnnouncement, { openModal = true } = {}) => {
      if (!nextAnnouncement?.enabled) {
        setAnnouncement(null);
        setModalOpen(false);
        return;
      }
      setAnnouncement(nextAnnouncement);
      if (!openModal) return;
      const revision = nextAnnouncement.updatedAt || 'current';
      setModalOpen(localStorage.getItem(getDismissalKey('modal', revision)) !== 'dismissed');
    };
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const locationId = getSelectedLocationId();
    if (locationId) {
      const token = user.token;
      const endpoint = `${config.BASEURL}announcement/locations/${locationId}`;
      DataGetApi(endpoint, {}, token).then((response) => {
        updateAnnouncement(response?.data?.announcement);
      });
    }

    const handleAnnouncementUpdated = (event) => updateAnnouncement(event.detail, { openModal: false });
    window.addEventListener(ANNOUNCEMENT_UPDATED_EVENT, handleAnnouncementUpdated);
    return () => window.removeEventListener(ANNOUNCEMENT_UPDATED_EVENT, handleAnnouncementUpdated);
  }, []);

  const dismiss = () => {
    const revision = announcement.updatedAt || 'current';
    localStorage.setItem(getDismissalKey('modal', revision), 'dismissed');
    setModalOpen(false);
  };

  if (!announcement) {
    return null;
  }

  return (
    <>
      <section className="announcementBanner" role="status">
        <div>
          <strong>{announcement.title}</strong>
          <span>{renderAnnouncementMessage(announcement.message)}</span>
        </div>
        <Button
          className="announcementBannerButton"
          color="inherit"
          onClick={() => setModalOpen(true)}
          size="small"
          variant="outlined"
        >
          View details
        </Button>
      </section>
      <Dialog open={modalOpen} onClose={dismiss} aria-labelledby="announcement-dialog-title">
        <DialogTitle id="announcement-dialog-title" className="announcementDialogTitle">
          {announcement.title}
          <IconButton aria-label="Close announcement" className="announcementDialogClose" onClick={dismiss}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <div className="announcementDialogMessage">
            {renderAnnouncementMessage(announcement.modalMessage || announcement.message)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Announcement;
